/**
 * Maps backend handoff trigger codes (snake_case) to human-readable Spanish labels.
 * The backend emits these inside agent_note lines like "Motivo: stage_requires_human (immediate)".
 */
export const HANDOFF_TRIGGER_LABELS: Record<string, string> = {
  stage_requires_human: 'La etapa requiere atención humana',
  user_requested: 'El lead solicitó hablar con un agente',
  lead_requested_human: 'El lead solicitó hablar con un agente',
  safety_crisis: 'Situación de crisis detectada',
  complex_objection: 'Objeción compleja detectada',
  price_negotiation: 'Negociación de precio',
  ready_to_close: 'Listo para cierre',
  media_requires_human: 'El cliente envió un archivo multimedia',
  turn_count_safety_net: 'Límite de turnos alcanzado',
  immediate: 'urgente',
  standard: 'estándar',
};

/**
 * Stable identifier for escalation notes stored in the DB.
 * Used for matching existing notes — do NOT change without a data migration.
 */
export const SLB_ESCALATION_NOTE_TITLE = 'SmartLeadbot: escalación';

/**
 * Stable identifier for funnel-move notes persisted by messaging (RET-1311).
 * Matched in NoteBubble to render the lilac funnel-move treatment on reload —
 * do NOT change without coordinating with the messaging note producer.
 */
export const SLB_FUNNEL_MOVE_NOTE_TITLE = 'SmartLeadbot: cambio de embudo';

export const HANDOFF_URGENCY_LABELS: Record<string, string> = {
  immediate: 'urgente',
  standard: 'estándar',
};

/**
 * Rewrites the "Motivo: <trigger> (<urgency>)" first line of an agent_note
 * produced by chatbot-service into a readable Spanish string.
 */
export function localizeAgentNote(raw: string): string {
  return raw.replace(
    /^Motivo:\s*(\S+?)(?:\s*\((\S+)\))?\s*$/m,
    (_, trigger: string, urgency?: string) => {
      const triggerLabel = HANDOFF_TRIGGER_LABELS[trigger] ?? trigger;
      const urgencyLabel = urgency ? (HANDOFF_URGENCY_LABELS[urgency] ?? urgency) : null;
      return `Motivo: ${triggerLabel}${urgencyLabel ? ` (${urgencyLabel})` : ''}`;
    },
  );
}

export const TONE_OPTIONS = [
  { value: 'professional', label: 'Profesional', description: 'Claro y directo' },
  { value: 'amigable', label: 'Amigable', description: 'Cálido y cercano' },
  { value: 'formal', label: 'Formal', description: 'Serio y corporativo' },
  { value: 'casual', label: 'Casual', description: 'Relajado y natural' },
  { value: 'empático', label: 'Empático', description: 'Comprensivo y atento' },
  { value: 'persuasivo', label: 'Persuasivo', description: 'Convincente y orientado al cierre' },
] as const;

export type tToneValue = typeof TONE_OPTIONS[number]['value'];

/**
 * Maps a legacy or display-string tone value to its canonical API value.
 * Matches by exact value first, then by case-insensitive label.
 * Returns the original string if no match is found.
 */
export function normalizeTone(tone: string): string {
  return (
    TONE_OPTIONS.find(
      (o) => o.value === tone || o.label.toLowerCase() === tone.toLowerCase(),
    )?.value ?? tone
  );
}
